/**
 * useTimezone Hook
 * 
 * Custom React hook that provides timezone utilities configured with the current user's timezone
 */

import { useMemo } from 'react';
import { useAuth } from '../useAuth';
import {
  getUserTimezone,
  toLocalDateInput,
  formatCalendarDate,
  formatDateTimeDisplay,
  fromLocalDateInput,
  getCurrentDateInTimezone,
  getDaysRemaining,
  formatRelativeTime,
} from '../utils/timezone';

export function useTimezone() {
  const { user } = useAuth();
  
  const timezone = useMemo(() => getUserTimezone(user), [user]);
  
  return useMemo(() => ({
    timezone,
    
    /**
     * Convert a date to YYYY-MM-DD format for date inputs
     */
    toDateInput: (date: string | Date) => toLocalDateInput(date, timezone),
    
    /**
     * Format a calendar-only date for display (MM/DD/YYYY) — classdate,
     * cyclestart/cycleend, etc. Deliberately does NOT apply any timezone
     * conversion (see formatCalendarDate's docs in utils/timezone.ts) —
     * every current caller passes a bare date, never a real timestamp.
     */
    formatDate: (date: string | Date) => formatCalendarDate(date),
    
    /**
     * Format a datetime for display
     */
    formatDateTime: (date: string | Date, includeSeconds = false) => 
      formatDateTimeDisplay(date, timezone, includeSeconds),
    
    /**
     * Convert a local date input to ISO string
     */
    fromDateInput: (dateString: string) => fromLocalDateInput(dateString, timezone),
    
    /**
     * Get current date in user's timezone
     */
    getCurrentDate: () => getCurrentDateInTimezone(timezone),
    
    /**
     * Calculate days remaining until a date
     */
    getDaysUntil: (endDate: string | Date) => getDaysRemaining(endDate, timezone),
    
    /**
     * Format relative time (e.g., "2 days ago")
     */
    formatRelative: (date: string | Date) => formatRelativeTime(date, timezone),
  }), [timezone]);
}
